(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-florame-account-center]';
  var TRIGGER_SELECTOR = '[data-florame-account-trigger]';
  var AUTH_TRIGGER_SELECTOR = '[data-florame-auth-trigger]';
  var CUSTOMER_LOGIN_PATH = '/customer_authentication/login';
  var HAPTICS_KEY = 'florame_haptics_enabled';
  var THEME_KEY = 'florame-theme-preference';
  var WEB_PUSH_HASH_KEY = 'florame_web_push_subscription_hash';
  var LANGUAGE_KEYS = ['florame_native_lang', 'bouquetLanguage'];
  var WEB_CUSTOMER_ID_KEY = 'florame_customer_id';
  var WEB_CUSTOMER_EMAIL_KEY = 'florame_customer_email';
  var AUTH_CONTEXT_KEY = 'florame_account_auth_context_v1';
  var LAST_GENERATION_KEY = 'florame_generation_result_job_v1';
  var LAST_GENERATION_ACTIVE_KIND_KEY = 'florame_generation_active_kind_v1';
  var LAST_GENERATION_ARCHIVE_KEY_PREFIX = 'florame_last_generation_v2:';
  var LAST_GENERATION_URL_PARAM = 'florame_bg_job';
  var LAST_GENERATION_MAX_AGE_MS = 7 * 86400000;
  var LAST_GENERATION_SYNC_INTERVAL_MS = 12000;
  var AUTH_PROBE_INTERVAL_MS = 60000;
  var instances = [];
  var authProbePromise = null;
  var lastAuthProbeAt = 0;
  var lastGenerationSyncPromise = null;
  var lastGenerationSyncAt = 0;
  var lastGenerationSyncOwner = '';
  var pendingPointerTrigger = null;
  var pointerActivationTimer = 0;
  var avatarEditorPromise = null;
  var accountStylePromise = null;

  function accountStylesReady() {
    return Array.from(document.querySelectorAll('link[data-florame-account-styles],link[data-florame-home-full]')).every(function (link) { return !!link.sheet && (!link.hasAttribute('data-florame-home-full') || link.dataset.florameHomeReady === 'true'); });
  }

  function loadAccountStyle(link) {
    if (link.sheet) return Promise.resolve();
    if (link.__florameAccountStyleLoad) return link.__florameAccountStyleLoad;
    var task = new Promise(function (resolve, reject) {
      function finish(error) {
        clearTimeout(timer);
        link.removeEventListener('load', onLoad);
        link.removeEventListener('error', onError);
        if (error) {
          link.removeAttribute('href'); link.removeAttribute('rel');
          reject(error);
        } else resolve();
      }
      function onLoad() { finish(); }
      function onError() { finish(new Error('account_styles_unavailable')); }
      var timer = setTimeout(onError, 15000);
      link.addEventListener('load', onLoad);
      link.addEventListener('error', onError);
      link.rel = 'stylesheet';
      link.href = link.getAttribute('data-href');
    }).finally(function () { if (link.__florameAccountStyleLoad === task) delete link.__florameAccountStyleLoad; });
    link.__florameAccountStyleLoad = task;
    return task;
  }

  function ensureAccountStyles() {
    if (accountStylesReady()) return Promise.resolve();
    if (accountStylePromise) return accountStylePromise;
    accountStylePromise = Promise.all([window.FlorameHomeStyles?.ensure(), ...Array.from(document.querySelectorAll('link[data-florame-account-styles]')).map(loadAccountStyle)])
      .catch(function (error) { accountStylePromise = null; throw error; });
    return accountStylePromise;
  }

  function refreshDraft(instance) {
    var link = instance.root.querySelector('[data-fac-route="drafts"]');
    if (link) link.hidden = !window.FlorameCreateDraft?.get?.();
  }

  function openAvatar(instance, trigger) {
    if (!instance || instance.root.hidden) return;
    if (window.FlorameAvatarEditor) { window.FlorameAvatarEditor.open(instance, trigger); return; }
    if (trigger) trigger.setAttribute('aria-busy','true');
    if (!avatarEditorPromise) avatarEditorPromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = instance.root.dataset.avatarEditorUrl;
      var timer = setTimeout(function () { script.remove(); reject(new Error('avatar_editor_timeout')); }, 12000);
      script.onload = function () { clearTimeout(timer); if (window.FlorameAvatarEditor) resolve(); else reject(new Error('avatar_editor_unavailable')); };
      script.onerror = function () { clearTimeout(timer); script.remove(); reject(new Error('avatar_editor_unavailable')); };
      document.head.appendChild(script);
    }).catch(function (error) { avatarEditorPromise = null; throw error; });
    avatarEditorPromise.then(function () {
      if (!instance.root.hidden && instance.root.classList.contains('is-open')) window.FlorameAvatarEditor.open(instance, trigger);
    }).catch(function () {
      window.FlorameUI?.toast({tone:'error',title:languageOf(instance.root) === 'en' ? 'Avatar unavailable' : 'Avatar non disponibile',detail:languageOf(instance.root) === 'en' ? 'Check your connection and try again.' : 'Controlla la connessione e riprova.'});
    }).finally(function () { if (trigger) trigger.removeAttribute('aria-busy'); });
  }

  var COPY = {
    it: {
      checking: 'Controllo disponibilità…',
      active: 'Attive',
      inactive: 'Da attivare',
      complete: 'Da completare',
      denied: 'Bloccate nelle impostazioni',
      deniedHelp: 'Apri il lucchetto del browser, consenti le notifiche e riprova',
      unsupported: 'Non disponibili su questo dispositivo',
      unavailable: 'Servizio momentaneamente non disponibile',
      installFirst: 'Installa Florame per attivarle',
      nativeAction: 'Tocca per verificare e attivare',
      nativeActive: 'Attive sul dispositivo',
      notificationsError: 'Non è stato possibile aggiornare le notifiche',
      hapticsOn: 'Attiva',
      hapticsOff: 'Spenta',
      hapticsUnsupported: 'Non supportata su questo dispositivo',
      installing: 'Apro le istruzioni…',
      helpTitle: 'Supporto florame.ai',
      helpDescription: 'FAQ, contatti email diretti e chat quando ti serve.',
      chatOpening: 'Apro la chat…',
      chatUnavailable: 'La chat non è disponibile in questo momento. Riprova tra poco.'
    },
    en: {
      checking: 'Checking availability…',
      active: 'On',
      inactive: 'Ready to enable',
      complete: 'Setup incomplete',
      denied: 'Blocked in device settings',
      deniedHelp: 'Open the browser lock, allow notifications, then try again',
      unsupported: 'Not available on this device',
      unavailable: 'Service temporarily unavailable',
      installFirst: 'Install Florame to enable them',
      nativeAction: 'Tap to check and enable',
      nativeActive: 'On for this device',
      notificationsError: 'Notifications could not be updated',
      hapticsOn: 'On',
      hapticsOff: 'Off',
      hapticsUnsupported: 'Not supported on this device',
      installing: 'Opening instructions…',
      helpTitle: 'florame.ai support',
      helpDescription: 'FAQs, direct email contacts and chat when you need it.',
      chatOpening: 'Opening chat…',
      chatUnavailable: 'Chat is unavailable right now. Please try again shortly.'
    }
  };

  function storageGet(key) {
    try { return window.localStorage.getItem(key); } catch (_) { return null; }
  }

  function storageSet(key, value) {
    try { window.localStorage.setItem(key, String(value)); } catch (_) {}
  }

  function storageRemove(key) {
    try { window.localStorage.removeItem(key); } catch (_) {}
  }

  function lastGenerationCustomerId(root) {
    if (!rootIsAuthenticated(root)) return '';
    var customer = window.__loggedCustomer || {};
    return String(customer.id || storageGet(WEB_CUSTOMER_ID_KEY) || '').trim();
  }

  function lastGenerationArchiveKey(root) {
    return LAST_GENERATION_ARCHIVE_KEY_PREFIX + (lastGenerationCustomerId(root) || 'guest');
  }

  function parsedLastGeneration(raw, maxAgeMs) {
    if (!raw) return null;
    try {
      var job = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!job || typeof job !== 'object') return null;
      var status = String(job.status || '').toLowerCase();
      if (status === 'error' || status === 'cancelled' || status === 'canceled') return null;
      var updatedAt = Number(job.updatedAt || job.startedAt || job.productStartedAt || 0);
      if (maxAgeMs && updatedAt && Date.now() - updatedAt > maxAgeMs) return null;
      if (!(job.requestId || job.createRequestId || job.bouquetId || job.imageUrl || job.complete || job.returnUrl)) return null;
      return job;
    } catch (_) {
      return null;
    }
  }

  function emit(name, detail) {
    try { window.dispatchEvent(new CustomEvent(name, { detail: detail || {} })); } catch (_) {}
  }

  function readLastGeneration(root) {
    var active = parsedLastGeneration(storageGet(LAST_GENERATION_KEY), LAST_GENERATION_MAX_AGE_MS);
    var archived = parsedLastGeneration(storageGet(lastGenerationArchiveKey(root)), 0);
    if (!active) return archived;
    if (!archived) return active;
    return Number(active.updatedAt || 0) >= Number(archived.updatedAt || 0) ? active : archived;
  }

  function lastGenerationTitle(job, root) {
    var marketing = job && job.productData && job.productData.marketingContent;
    var title = marketing && marketing.title || job && job.productData && job.productData.title || job && job.title || '';
    title = String(title || '').replace(/\s+/g, ' ').trim();
    if (title && !/generazione|generation/i.test(title)) return title.slice(0, 90);
    return root && root.getAttribute('data-language') === 'en' ? 'Your latest bouquet' : 'Il tuo ultimo bouquet';
  }

  function lastGenerationStatus(job, root) {
    var english = root && root.getAttribute('data-language') === 'en';
    var done = !!job && (String(job.status || '').toLowerCase() === 'done' || job.complete === true);
    if (done) return english ? 'Ready · View bouquet' : 'Pronto · Visualizza bouquet';
    return english ? 'Generating · Follow the progress' : 'In generazione · Segui l’avanzamento';
  }

  function lastGenerationImage(job) {
    var product = job && job.productData || {};
    return String(job && job.imageUrl || product.imageUrl || product.bouquetImage || product.image || '').trim();
  }

  function refreshLastGeneration(instance) {
    if (!instance || !instance.lastGeneration) return null;
    var job = readLastGeneration(instance.root);
    instance.lastGeneration.hidden = !job;
    instance.lastGenerationJob = job;
    if (!job) return null;
    if (instance.lastGenerationTitle) instance.lastGenerationTitle.textContent = lastGenerationTitle(job, instance.root);
    if (instance.lastGenerationStatus) instance.lastGenerationStatus.textContent = lastGenerationStatus(job, instance.root);
    var imageUrl = lastGenerationImage(job);
    if (instance.lastGenerationImage) {
      instance.lastGenerationImage.hidden = !imageUrl;
      if (imageUrl) {
        if (instance.lastGenerationImage.getAttribute('src') !== imageUrl) instance.lastGenerationImage.src = imageUrl;
      }
      else instance.lastGenerationImage.removeAttribute('src');
    }
    if (instance.lastGenerationFallback) instance.lastGenerationFallback.hidden = !!imageUrl;
    if (instance.lastGenerationOpen) {
      instance.lastGenerationOpen.setAttribute('aria-label', (instance.root.getAttribute('data-language') === 'en' ? 'Open latest generation: ' : 'Riapri ultima generazione: ') + lastGenerationTitle(job, instance.root));
    }
    return job;
  }

  function lastGenerationApi(instance, action, body) {
    var endpoint = instance && instance.root && instance.root.getAttribute('data-loyalty-endpoint') || '/apps/florame-messages';
    var url = new URL(endpoint, window.location.origin);
    url.searchParams.set('action', action);
    var controller = new AbortController();
    var timeout = window.setTimeout(function () { controller.abort(); }, 8000);
    return window.fetch(url.toString(), {
      signal: controller.signal,
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(Object.assign({ action: action }, body)) : undefined
    }).then(function (response) {
      return response.json().catch(function () { return null; }).then(function (payload) {
        if (!response.ok || !payload || payload.success === false) throw new Error(payload && payload.error || 'generation_sync_unavailable');
        return payload;
      });
    }).finally(function () { window.clearTimeout(timeout); });
  }

  function syncLastGeneration(instance, options) {
    if (!instance || !rootIsAuthenticated(instance.root) || !lastGenerationCustomerId(instance.root)) return Promise.resolve(null);
    var owner = lastGenerationCustomerId(instance.root);
    if (lastGenerationSyncOwner !== owner) {
      lastGenerationSyncOwner = owner;
      lastGenerationSyncAt = 0;
      lastGenerationSyncPromise = null;
    }
    var stillOwner = function () { return lastGenerationCustomerId(instance.root) === owner; };
    var now = Date.now();
    if (lastGenerationSyncPromise) return lastGenerationSyncPromise;
    if (!(options && options.force) && now - lastGenerationSyncAt < LAST_GENERATION_SYNC_INTERVAL_MS) return Promise.resolve(readLastGeneration(instance.root));
    lastGenerationSyncAt = now;
    var local = readLastGeneration(instance.root);
    var pending = lastGenerationApi(instance, 'generation_latest').then(function (payload) {
      if (!stillOwner()) return null;
      // Read again after the network: another tab or generation may have advanced.
      local = readLastGeneration(instance.root);
      var remote = parsedLastGeneration(payload && payload.generation, 0);
      var localUpdatedAt = Number(local && local.updatedAt || 0);
      var remoteUpdatedAt = Number(remote && remote.updatedAt || 0);
      if (remote && remoteUpdatedAt > localUpdatedAt) {
        storageSet(lastGenerationArchiveKey(instance.root), JSON.stringify(remote));
        refreshLastGeneration(instance);
        emit('florame:last-generation-restored', { source: 'account-cloud', generation: remote });
        return remote;
      }
      if (local && (local.complete === true || String(local.status || '').toLowerCase() === 'done') && localUpdatedAt > remoteUpdatedAt) {
        return lastGenerationApi(instance, 'generation_save', { generation: local }).then(function (saved) {
          if (!stillOwner()) return null;
          var newest = readLastGeneration(instance.root);
          if (newest && Number(newest.updatedAt || 0) > Number(local.updatedAt || 0)) {
            refreshLastGeneration(instance);
            return newest;
          }
          var canonical = parsedLastGeneration(saved && saved.generation, 0) || local;
          storageSet(lastGenerationArchiveKey(instance.root), JSON.stringify(canonical));
          refreshLastGeneration(instance);
          return canonical;
        });
      }
      refreshLastGeneration(instance);
      return remote || local;
    }).catch(function () {
      if (!stillOwner()) return null;
      refreshLastGeneration(instance);
      return readLastGeneration(instance.root);
    }).finally(function () {
      if (lastGenerationSyncPromise === pending) lastGenerationSyncPromise = null;
    });
    lastGenerationSyncPromise = pending;
    return pending;
  }

  function lastGenerationTarget(job) {
    var appPath = window.__florameThemeConfig && window.__florameThemeConfig.appPath || '/pages/florame-app';
    var fallback = appPath + '?mode=create';
    var target;
    try {
      target = new URL(job && job.returnUrl || fallback, window.location.origin);
      var appUrl = new URL(appPath, window.location.origin);
      if (target.origin !== window.location.origin || target.pathname.replace(/\/+$/, '') !== appUrl.pathname.replace(/\/+$/, '')) target = new URL(fallback, window.location.origin);
    } catch (_) {
      target = new URL(fallback, window.location.origin);
    }
    target.searchParams.set('mode', 'create');
    target.searchParams.set('resultJob', 'open');
    var bouquetId = job && (job.bouquetId || job.productData && job.productData.bouquetId) || target.searchParams.get('bouquet') || '';
    if (bouquetId) target.searchParams.set('bouquet', String(bouquetId));
    var payload = {
      v: job && job.v || 1,
      kind: 'result',
      storageKey: LAST_GENERATION_KEY,
      returnUrl: target.pathname + target.search,
      requestId: job && job.requestId || '',
      createRequestId: job && job.createRequestId || '',
      status: job && job.status || 'running',
      progress: Number(job && job.progress || 0),
      updatedAt: Date.now(),
      complete: !!(job && job.complete),
      muted: false,
      bouquetId: bouquetId || null,
      imageUrl: lastGenerationImage(job) || null
    };
    target.searchParams.set(LAST_GENERATION_URL_PARAM, JSON.stringify(payload));
    return target.toString();
  }

  function openLastGeneration(instance) {
    var job = refreshLastGeneration(instance);
    if (!job) return;
    job.updatedAt = Date.now();
    job.muted = false;
    var target = lastGenerationTarget(job);
    var direct = !!(window.__AWI_CREATE_READY && window.__AWI_INIT_DONE && new URL(target).pathname === window.location.pathname);
    window.__florameBackgroundFocusJob = { key: LAST_GENERATION_KEY, kind: 'result', ts: Date.now(), opening: true, navigating: !direct };
    var bouquetId = new URL(target).searchParams.get('bouquet');
    if (bouquetId) job.bouquetId = bouquetId;
    try {
      storageSet(LAST_GENERATION_KEY, JSON.stringify(job));
      storageSet(LAST_GENERATION_ACTIVE_KIND_KEY, 'result');
      storageSet(lastGenerationArchiveKey(instance.root), JSON.stringify(job));
    } catch (_) {}
    emit('florame:last-generation-open', { job: job, target: target });
    // Focus the mounted result before closing the menu, without a bubble flash.
    if (direct) emit('florame:background-job-focus', { job: job, storageKey: LAST_GENERATION_KEY });
    instance.close(false);
    if (!direct) window.location.assign(target);
  }

  function isNativeAccountActive() {
    try {
      if (window.__loggedCustomer && window.__loggedCustomer.nativeApp) return true;
      if (document.documentElement.getAttribute('data-florame-native-signed-in') === 'true') return true;
      return storageGet('florame_native_signed_in') === '1';
    } catch (_) { return false; }
  }

  function rootIsAuthenticated(root) {
    return !!root && root.getAttribute('data-authenticated') === 'true';
  }

  function applyWebCustomerIdentity(customerId, customerEmail) {
    var normalizedId = customerId == null ? '' : String(customerId).trim();
    var normalizedEmail = customerEmail == null ? '' : String(customerEmail).trim().toLowerCase();

    if (normalizedId) storageSet(WEB_CUSTOMER_ID_KEY, normalizedId);
    else storageRemove(WEB_CUSTOMER_ID_KEY);
    if (normalizedEmail) storageSet(WEB_CUSTOMER_EMAIL_KEY, normalizedEmail);
    else storageRemove(WEB_CUSTOMER_EMAIL_KEY);

    try {
      var loggedCustomer = window.__loggedCustomer && typeof window.__loggedCustomer === 'object'
        ? window.__loggedCustomer
        : {};
      loggedCustomer.id = normalizedId || null;
      loggedCustomer.email = normalizedEmail || null;
      window.__loggedCustomer = loggedCustomer;
      window.AWI_CUSTOMER_EMAIL = normalizedEmail || null;
      window.__AWI_CUSTOMER_EMAIL = normalizedEmail || null;
      window.customerEmail = normalizedEmail || null;
      window.__customerEmail = normalizedEmail || null;
      window.HOMEPAGE_INIT_CUSTOMER_EMAIL = normalizedEmail || null;
      if (window.customerData && typeof window.customerData === 'object') {
        window.customerData.email = normalizedEmail || null;
      }
      if (normalizedEmail) document.documentElement.setAttribute('data-customer-email', normalizedEmail);
      else document.documentElement.removeAttribute('data-customer-email');
    } catch (_) {}

    try {
      if (window.__florameWebPushConfig) {
        window.__florameWebPushConfig.customerId = normalizedId || null;
        window.__florameWebPushConfig.customerEmail = normalizedEmail || null;
      }
    } catch (_) {}
  }

  function syncClientAuthFromServer(instance) {
    if (!instance || !instance.root) return;
    var authenticated = rootIsAuthenticated(instance.root);
    var customer = window.__loggedCustomer || {};
    var customerId = authenticated ? String(instance.root.dataset.customerId || customer.id || '').trim() : '';
    var customerEmail = authenticated && customer.email ? String(customer.email).trim() : '';

    if (!authenticated && isNativeAccountActive()) {
      emit('florame:auth-state', {
        authenticated: true,
        customerId: customer.id || null,
        customerEmail: customer.email || null,
        source: 'native-app'
      });
      return;
    }

    applyWebCustomerIdentity(customerId, customerEmail);
    emit('florame:auth-state', {
      authenticated: authenticated,
      customerId: customerId || null,
      customerEmail: customerEmail || null,
      source: 'shopify-server'
    });
    if (authenticated && customerId) syncLastGeneration(instance);
  }

  function authProbeUrl() {
    return '/apps/florame-messages?action=account_session';
  }

  function authIdentity(root) {
    return rootIsAuthenticated(root) ? String(root.dataset.customerId || window.__loggedCustomer?.id || '').replace(/^gid:\/\/shopify\/Customer\//, '') : '';
  }

  function ensureAuthFresh(instance, options) {
    if (!instance || !instance.root || isNativeAccountActive() || typeof window.fetch !== 'function') {
      return Promise.resolve(true);
    }
    var force = !!(options && options.force);
    var now = Date.now();
    if (authProbePromise) return authProbePromise;
    if (!force && now - lastAuthProbeAt < AUTH_PROBE_INTERVAL_MS) return Promise.resolve(true);
    lastAuthProbeAt = now;
    var renderedIdentity = authIdentity(instance.root);
    var controller = new AbortController();
    var timeout = window.setTimeout(function () { controller.abort(); }, 8000);
    // The signed proxy checks the session without fetching/parsing the entire
    // page, reading a profile, or treating cached guest HTML as a logout.
    authProbePromise = window.fetch(authProbeUrl(), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      signal: controller.signal,
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      return response.json().then(function (payload) {
        if (response.ok && payload.success === true && /^(?:gid:\/\/shopify\/Customer\/)?\d+$/.test(String(payload.customerId || ''))) return String(payload.customerId).replace(/^gid:\/\/shopify\/Customer\//, '');
        // A proxy/configuration/network failure is not proof of a logout.
        if (response.status === 401 && payload.error === 'CUSTOMER_MESSAGE_CUSTOMER_REQUIRED') return '';
        return null;
      });
    }).then(function (serverIdentity) {
      if (serverIdentity == null || isNativeAccountActive() || authIdentity(instance.root) !== renderedIdentity) return true;
      emit('florame:auth-confirmed', { authenticated: !!serverIdentity, source: 'signed-proxy' });
      if (serverIdentity !== renderedIdentity) {
        emit('florame:auth-stale', {
          renderedAuthenticated: !!renderedIdentity,
          serverAuthenticated: !!serverIdentity
        });
        window.location.reload();
        return false;
      }
      return true;
    }).catch(function () {
      return true;
    }).finally(function () {
      window.clearTimeout(timeout);
      authProbePromise = null;
    });
    return authProbePromise;
  }

  function waitForShopifyInboxToggle() {
    var controller = window.FlorameShopifyInbox;
    if (controller && typeof controller.waitForToggle === 'function') return controller.waitForToggle();
    return Promise.resolve(null);
  }

  function languageOf(root) {
    var raw = (root && root.getAttribute('data-language')) || storageGet(LANGUAGE_KEYS[0]) || document.documentElement.lang || 'it';
    return String(raw).toLowerCase().indexOf('en') === 0 ? 'en' : 'it';
  }

  function copyFor(root) {
    return COPY[languageOf(root)];
  }

  function currentLanguage() {
    var value = storageGet(LANGUAGE_KEYS[0]) || storageGet(LANGUAGE_KEYS[1]) || document.documentElement.lang || 'it';
    return String(value).slice(0, 2).toLowerCase();
  }

  function partnerSlug() {
    try {
      var params = new URLSearchParams(window.location.search);
      var query = params.get('partner') || params.get('partnerSlug');
      if (query) return String(query).trim();
    } catch (_) {}
    try {
      var context = window.FloramePartner && typeof window.FloramePartner.getContext === 'function'
        ? window.FloramePartner.getContext()
        : null;
      if (context && context.active && context.slug) return String(context.slug).trim();
    } catch (_) {}
    return '';
  }

  function safeCurrentReturnTo() {
    var value = String(window.location.pathname || '/') + String(window.location.search || '') + String(window.location.hash || '');
    return value.indexOf('//') === 0 || /^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/(account|authentication|customer_authentication|customer_identity)(?:\/|$)/i.test(window.location.pathname) ? '/' : value;
  }

  function safeReturnTo(value) {
    var fallback = safeCurrentReturnTo();
    if (!value) return fallback;
    try {
      var url = new URL(String(value), window.location.origin);
      if (url.origin !== window.location.origin || url.username || url.password || /\\|[\u0000-\u001f]/.test(String(value))
        || /^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/(account|authentication|customer_authentication|customer_identity)(?:\/|$)/i.test(url.pathname)) return fallback;
      var result = String(url.pathname || '/') + String(url.search || '') + String(url.hash || '');
      return result.indexOf('//') === 0 ? fallback : result;
    } catch (_) { return fallback; }
  }

  function authReturnTo(root) {
    return safeReturnTo(root && root.getAttribute('data-fac-auth-return-to'));
  }

  function decorateLink(anchor) {
    if (!anchor || !anchor.href) return;
    try {
      var url = new URL(anchor.href, window.location.origin);
      var slug = partnerSlug();
      var lang = currentLanguage();
      var authRoute = anchor.getAttribute('data-fac-auth-route');
      if (['favorites', 'creations'].indexOf(anchor.getAttribute('data-fac-route')) !== -1) {
        url.pathname = lang === 'en' ? '/en' : '/';
      }
      var isAccountHost = String(url.hostname || '').toLowerCase() === 'account.florame.ai';

      if (slug && !isAccountHost && !url.searchParams.has('partner') && !url.searchParams.has('partnerSlug')) {
        url.searchParams.set('partner', slug);
      }
      if (lang && !url.searchParams.has('lang')) url.searchParams.set('lang', lang);
      if (authRoute === 'login' || authRoute === 'register') {
        url.searchParams.set('return_to', authReturnTo(anchor.closest(ROOT_SELECTOR)));
      }
      anchor.href = url.toString();
    } catch (_) {}
  }

  function decorateLinks(root) {
    if (!root) return;
    Array.prototype.forEach.call(root.querySelectorAll('a[data-fac-route], a[data-fac-auth-route]'), decorateLink);
  }

  function rememberAuthContext(root, options) {
    var opts = options || {};
    var returnTo = safeReturnTo(opts.returnTo);
    var intent = String(opts.intent || 'account').trim().slice(0, 80) || 'account';
    if (root) {
      root.setAttribute('data-fac-auth-return-to', returnTo);
      root.setAttribute('data-fac-auth-intent', intent);
    }
    try {
      window.sessionStorage.setItem(AUTH_CONTEXT_KEY, JSON.stringify({
        returnTo: returnTo,
        intent: intent,
        scrollY: Math.max(0, Math.round(window.scrollY || 0)),
        savedAt: Date.now()
      }));
    } catch (_) {}
    return { returnTo: returnTo, intent: intent };
  }

  function restoreAuthContext(instance) {
    if (!instance || !rootIsAuthenticated(instance.root)) return;
    var saved = null;
    try { saved = JSON.parse(window.sessionStorage.getItem(AUTH_CONTEXT_KEY) || 'null'); } catch (_) {}
    if (!saved || safeReturnTo(saved.returnTo) !== safeCurrentReturnTo()) return;
    try { window.sessionStorage.removeItem(AUTH_CONTEXT_KEY); } catch (_) {}
    if (!Number.isFinite(Number(saved.scrollY))) return;
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () { window.scrollTo(0, Math.max(0, Number(saved.scrollY))); });
    });
  }

  function focusables(panel) {
    if (!panel) return [];
    return Array.prototype.filter.call(
      panel.querySelectorAll('a[href], button:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      function (node) { return !node.hidden && node.offsetParent !== null && node.getAttribute('aria-hidden') !== 'true'; }
    );
  }

  function setSwitch(button, options) {
    if (!button) return;
    var opts = options || {};
    button.classList.toggle('is-on', !!opts.on);
    button.classList.toggle('is-checking', !!opts.checking);
    button.classList.toggle('is-error', !!opts.error);
    button.classList.toggle('is-action', !!opts.action);
    button.disabled = !!opts.disabled;
    if (button.hasAttribute('data-fac-haptics')) button.setAttribute('aria-checked', opts.on ? 'true' : 'false');
    else button.setAttribute('aria-pressed', opts.on ? 'true' : 'false');
    var status = button.querySelector('[data-fac-notifications-status], [data-fac-haptics-status]');
    if (status && opts.label) status.textContent = opts.label;
  }

  function pushLabel(root, state) {
    var c = copyFor(root);
    if (!state || state.supported === false) return c.unsupported;
    if (state.loginRequired) return root.getAttribute('data-language') === 'en' ? 'Sign in to receive order alerts' : 'Accedi per ricevere gli avvisi degli ordini';
    if (state.error) return c.notificationsError;
    if (state.permission === 'denied') return c.denied;
    if (state.platform === 'native') return state.subscribed ? c.nativeActive : c.nativeAction;
    if (state.platform === 'ios' && state.standalone === false && !state.subscribed) return c.installFirst;
    if (state.subscribed) return c.active;
    if (state.optedOut) return c.inactive;
    if (state.permission === 'granted') return c.complete;
    return c.inactive;
  }

  function applyPushState(instance, state, options) {
    var button = instance.notifications;
    if (!button) return;
    var opts = options || {};
    var denied = state && state.permission === 'denied';
    var unsupported = state && state.supported === false;
    setSwitch(button, {
      on: !!(state && state.subscribed),
      checking: !!opts.checking,
      error: !!opts.error || !!(state && state.error) || denied,
      disabled: !!opts.checking || !!unsupported,
      action: !!opts.action || denied,
      label: opts.label || (opts.error ? copyFor(instance.root).notificationsError : pushLabel(instance.root, state))
    });
    instance.pushState = state || null;
  }

  function nativePushAvailable() {
    return !!(window.FlorameNative && window.FlorameNative.isNative && typeof window.FlorameNative.requestNotifications === 'function');
  }

  function renderLocation(instance, state) {
    if (!instance.location || !state) return;
    var en = instance.root.getAttribute('data-language') === 'en';
    var label = state.enabled === false ? (en ? 'GPS off · tap to enable' : 'GPS spento · tocca per attivare')
      : state.permission === 'granted' ? (state.accuracy === 'approximate' ? (en ? 'Approximate location allowed' : 'Posizione approssimativa consentita') : (en ? 'Location allowed' : 'Posizione consentita'))
      : state.permission === 'denied' ? (en ? 'Blocked · tap to enable' : 'Bloccata · tocca per attivare')
      : (en ? 'Tap to allow location' : 'Tocca per consentire la posizione');
    instance.location.querySelector('[data-fac-location-status]').textContent = label;
  }

  function refreshLocation(instance) {
    var api = window.FlorameNative;
    if (!instance.location || !api || !api.isNative || api.canManagePermissions === false || typeof api.locationStatus !== 'function' || typeof api.requestLocationPermission !== 'function') return Promise.resolve();
    instance.location.hidden = false;
    return api.locationStatus().then(function (state) { renderLocation(instance, state); }).catch(function () {
      instance.location.querySelector('[data-fac-location-status]').textContent = copyFor(instance.root).unavailable;
    });
  }

  function requestLocationAccess(instance) {
    var api = window.FlorameNative;
    if (instance.location.disabled || !api || typeof api.requestLocationPermission !== 'function') return;
    instance.location.disabled = true;
    instance.location.querySelector('[data-fac-location-status]').textContent = copyFor(instance.root).checking;
    Promise.resolve(api.requestLocationPermission()).then(function (state) { renderLocation(instance, state); })
      .catch(function () { return refreshLocation(instance); })
      .finally(function () { instance.location.disabled = false; });
  }

  function immediateWebPushState(api) {
    if (api && typeof api.peekStatus === 'function') {
      try { return api.peekStatus(); } catch (_) {}
    }
    var supported = 'serviceWorker' in navigator && 'Notification' in window && 'PushManager' in window;
    var permission = 'Notification' in window ? Notification.permission : 'unsupported';
    return {
      supported: supported,
      permission: permission,
      subscribed: supported && permission === 'granted' && !!storageGet(WEB_PUSH_HASH_KEY),
      platform: 'web',
      verified: false
    };
  }

  function refreshPush(instance) {
    if (!instance.notifications) return Promise.resolve(null);
    var c = copyFor(instance.root);
    var refreshId = (instance.pushRefreshId || 0) + 1;
    instance.pushRefreshId = refreshId;
    if (nativePushAvailable()) {
      if (instance.pushState && instance.pushState.platform === 'native') applyPushState(instance, instance.pushState);
      else setSwitch(instance.notifications, { checking: true, label: c.checking });
      var nativeStatus = typeof window.FlorameNative.notificationStatus === 'function'
        ? window.FlorameNative.notificationStatus()
        : window.FlorameNative.requestNotifications({ prompt: false, source: 'account_center_status' });
      return Promise.resolve(nativeStatus)
        .then(function (state) {
          if (refreshId !== instance.pushRefreshId) return instance.pushState;
          var normalized = state || { supported: true, subscribed: false, permission: 'default', platform: 'native' };
          normalized.platform = 'native';
          instance.nativePushActive = !!normalized.subscribed;
          applyPushState(instance, normalized, { action: !normalized.subscribed });
          return normalized;
        })
        .catch(function (error) {
          if (refreshId !== instance.pushRefreshId) return instance.pushState;
          var latest = window.__florameNativePermissions && window.__florameNativePermissions.notifications;
          var fallback = Object.assign({}, instance.pushState || {}, latest || {}, { supported: true, subscribed: false, platform: 'native' });
          fallback.loginRequired = /Accedi|Sign in|collegate all.account/i.test(error && error.message || '');
          instance.nativePushActive = false;
          applyPushState(instance, fallback, { action: true, error: !fallback.loginRequired, label: fallback.loginRequired || fallback.permission === 'denied' ? pushLabel(instance.root, fallback) : c.notificationsError });
          return fallback;
        });
    }

    var api = window.FlorameWebPush;
    var immediate = immediateWebPushState(api);
    applyPushState(instance, immediate);
    if (!api || typeof api.status !== 'function') {
      return Promise.resolve(immediate);
    }

    return Promise.resolve(api.status()).then(function (state) {
      if (refreshId !== instance.pushRefreshId) return instance.pushState;
      applyPushState(instance, state || { supported: false });
      return state;
    }).catch(function () {
      if (refreshId !== instance.pushRefreshId) return instance.pushState;
      applyPushState(instance, instance.pushState || { supported: true }, { error: true, label: c.unavailable });
      return null;
    });
  }

  function togglePush(instance) {
    if (!instance || !instance.notifications || instance.notifications.disabled) return;
    instance.pushRefreshId = (instance.pushRefreshId || 0) + 1;
    var c = copyFor(instance.root);
    if (!nativePushAvailable() && instance.pushState && instance.pushState.permission === 'denied') {
      applyPushState(instance, instance.pushState, { action: true, error: true, label: c.deniedHelp });
      return;
    }
    if (nativePushAvailable()) {
      setSwitch(instance.notifications, { on: !!(instance.pushState && instance.pushState.subscribed), checking: true, disabled: true, action: true, label: c.checking });
      Promise.resolve(window.FlorameNative.requestNotifications({ prompt: true, source: 'account_center' }))
        .then(function (result) {
          var latest = window.__florameNativePermissions && window.__florameNativePermissions.notifications;
          var state = Object.assign({ supported: true }, result || {}, { platform: 'native' });
          if (latest && latest.permission !== 'granted') { state.permission = latest.permission; state.subscribed = false; }
          instance.nativePushActive = !!state.subscribed;
          applyPushState(instance, state, { action: !state.subscribed });
        })
        .catch(function (error) {
          var latest = window.__florameNativePermissions && window.__florameNativePermissions.notifications;
          var denied = latest && latest.permission === 'denied' || error && (error.permission === 'denied' || error.permission === 'prompt-with-rationale');
          instance.nativePushActive = false;
          var state = Object.assign({}, latest || {}, { supported: true, subscribed: false, permission: denied ? 'denied' : latest && latest.permission || 'default', platform: 'native' });
          state.loginRequired = /Accedi|Sign in|collegate all.account/i.test(error && error.message || '');
          applyPushState(instance, state, { action: true, error: !state.loginRequired, label: state.loginRequired ? pushLabel(instance.root, state) : denied ? c.denied : c.notificationsError });
        });
      return;
    }

    var api = window.FlorameWebPush;
    if (!api || typeof api.setEnabled !== 'function') {
      applyPushState(instance, { supported: true }, { error: true, label: c.unavailable });
      return;
    }
    var target = !(instance.pushState && instance.pushState.subscribed);
    setSwitch(instance.notifications, { on: target, checking: true, disabled: true, label: c.checking });
    Promise.resolve(api.setEnabled(target))
      .then(function (state) { applyPushState(instance, state || { supported: true, subscribed: target }); })
      .catch(function () { refreshPush(instance).then(function () {
        if (!instance.pushState || instance.pushState.permission !== 'denied') {
          setSwitch(instance.notifications, { on: !!(instance.pushState && instance.pushState.subscribed), error: true, label: instance.pushState && instance.pushState.loginRequired ? pushLabel(instance.root, instance.pushState) : c.notificationsError });
        }
      }); });
  }

  function hapticsSupported() {
    try {
      if (window.FlorameHaptics && typeof window.FlorameHaptics.supported === 'function') return window.FlorameHaptics.supported();
      if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return false;
      var userAgent = String(navigator.userAgent || '');
      var isIos = /iPad|iPhone|iPod/i.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      var isMobile = navigator.userAgentData && typeof navigator.userAgentData.mobile === 'boolean'
        ? navigator.userAgentData.mobile
        : /Android|Mobi/i.test(userAgent);
      return !isIos && isMobile;
    } catch (_) {
      return false;
    }
  }

  function hapticsEnabled() {
    try {
      if (window.FlorameHaptics && typeof window.FlorameHaptics.enabled === 'function') return window.FlorameHaptics.enabled();
    } catch (_) {}
    return storageGet(HAPTICS_KEY) !== '0';
  }

  function refreshHaptics(instance) {
    if (!instance.haptics) return;
    var c = copyFor(instance.root);
    instance.haptics.hidden = !hapticsSupported();
    if (instance.haptics.hidden) return;
    var enabled = hapticsEnabled();
    setSwitch(instance.haptics, { on: enabled, label: enabled ? c.hapticsOn : c.hapticsOff });
  }

  function toggleHaptics(instance) {
    if (!instance || !instance.haptics || instance.haptics.disabled) return;
    var next = !hapticsEnabled();
    try {
      if (window.FlorameHaptics && typeof window.FlorameHaptics.setEnabled === 'function') {
        next = window.FlorameHaptics.setEnabled(next);
      } else {
        storageSet(HAPTICS_KEY, next ? '1' : '0');
        emit('florame:hapticschange', { enabled: next });
      }
    } catch (_) {
      storageSet(HAPTICS_KEY, next ? '1' : '0');
    }
    refreshHaptics(instance);
    if (next && window.FlorameHaptics && typeof window.FlorameHaptics.vibrate === 'function') {
      try { window.FlorameHaptics.vibrate([12, 24, 12]); } catch (_) {}
    }
  }

  function refreshTheme(instance) {
    storageSet(THEME_KEY, 'light');
    document.documentElement.setAttribute('data-theme', 'light');
    if (instance.theme) instance.theme.value = 'light';
  }

  function syncInstall(instance, detail) {
    if (!instance.install) return;
    var api = window.FlorameWebPush;
    var native = !!(window.FlorameNative && window.FlorameNative.isNative);
    var installed = detail && typeof detail.installed === 'boolean'
      ? detail.installed
      : !!(api && typeof api.isInstalled === 'function' && api.isInstalled());
    var installable = detail && typeof detail.installable === 'boolean'
      ? detail.installable
      : document.documentElement.getAttribute('data-florame-pwa-installable') === '1';
    var mobileInstructions = !!(api && ((typeof api.isAppleTouchDevice === 'function' && api.isAppleTouchDevice()) || (typeof api.isAndroidDevice === 'function' && api.isAndroidDevice())));
    instance.install.hidden = native || installed || !api || typeof api.showInstall !== 'function' || (!installable && !mobileInstructions);
  }

  function openInstall(instance) {
    if (!instance.install || instance.install.hidden) return;
    var api = window.FlorameWebPush;
    if (!api || typeof api.showInstall !== 'function') return;
    instance.install.setAttribute('aria-busy', 'true');
    var status = instance.install.querySelector('small');
    var previous = status ? status.textContent : '';
    if (status) status.textContent = copyFor(instance.root).installing;
    try { api.showInstall(); } catch (_) {}
    window.setTimeout(function () {
      instance.install.removeAttribute('aria-busy');
      if (status) status.textContent = previous;
      syncInstall(instance);
    }, 900);
  }

  function changeLanguage(instance) {
    var select = instance.language;
    if (!select) return;
    var language = String(select.value || 'it').slice(0, 8);
    // Only expose locales whose custom Florame flows are ready.
    if (language !== 'it' && language !== 'en') return;
    if (window.FlorameLanguage) {
      window.FlorameLanguage.change(language, select.form);
      return;
    }
    LANGUAGE_KEYS.forEach(function (key) { storageSet(key, language); });
    var next = language.slice(0, 2).toLowerCase() === 'en' ? 'en' : 'it';
    instance.root.setAttribute('data-language', next);
    // Guides are authored in both languages on the server. Loading the matching
    // route preserves the complete copy, metadata and FAQ schema together.
    var guide = document.querySelector('[data-florame-guide-language]');
    if (guide && guide.getAttribute('data-florame-guide-language') !== next) {
      var guideUrl = new URL(window.location.href);
      var guidePath = guideUrl.pathname.replace(/^\/en(?=\/|$)/, '');
      guideUrl.pathname = (next === 'en' ? '/en' : '') + (guidePath || '/');
      guideUrl.searchParams.delete('lang');
      guideUrl.searchParams.delete('locale');
      window.location.assign(guideUrl.href);
      return;
    }
    if (typeof window.__florameLoadNativeI18n === 'function') {
      window.__florameLoadNativeI18n(next).catch(function () {});
    } else if (window.FlorameI18n && typeof window.FlorameI18n.setLanguage === 'function') {
      window.FlorameI18n.setLanguage(next);
    } else {
      document.documentElement.setAttribute('lang', next);
      emit('florame:native-language-change', { language: next });
    }
    refreshCapabilities(instance);
    refreshLastGeneration(instance);
  }

  function refreshCapabilities(instance) {
    if (!instance) return Promise.resolve();
    decorateLinks(instance.root);
    refreshHaptics(instance);
    refreshTheme(instance);
    syncInstall(instance);
    refreshLocation(instance);
    return refreshPush(instance);
  }

  function AccountCenter(root) {
    this.root = root;
    this.panel = root.querySelector('.fac__panel');
    this.notifications = root.querySelector('[data-fac-notifications]');
    this.location = root.querySelector('[data-fac-location]');
    this.haptics = root.querySelector('[data-fac-haptics]');
    this.theme = root.querySelector('[data-fac-theme]');
    this.language = root.querySelector('[data-fac-language]');
    this.install = root.querySelector('[data-fac-install]');
    this.helpButton = root.querySelector('[data-fac-help]');
    this.helpView = root.querySelector('[data-fac-help-view]');
    this.helpBack = root.querySelector('[data-fac-help-back]');
    this.chatButtons = root.querySelectorAll('[data-fac-chat]');
    this.chatStatus = root.querySelector('[data-fac-chat-status]');
    this.lastGeneration = root.querySelector('[data-fac-last-generation]');
    this.lastGenerationOpen = root.querySelector('[data-fac-last-generation-open]');
    this.lastGenerationImage = root.querySelector('[data-fac-last-generation-image]');
    this.lastGenerationFallback = root.querySelector('[data-fac-last-generation-fallback]');
    this.lastGenerationTitle = root.querySelector('[data-fac-last-generation-title]');
    this.lastGenerationStatus = root.querySelector('[data-fac-last-generation-status]');
    this.lastGenerationJob = null;
    this.title = root.querySelector('.fac__title');
    this.description = root.querySelector('.fac__description');
    this.scroll = root.querySelector('.fac__scroll');
    this.accountTitle = this.title ? this.title.textContent : '';
    this.accountDescription = this.description ? this.description.textContent : '';
    this.lastTrigger = null;
    this.restoreTriggerFocusOnClose = true;
    this.closeTimer = 0;
    this.openFrame = 0;
    this.pushState = null;
    this.pushRefreshId = 0;
    this.nativePushActive = false;
    this.dragState = null;
    this.bind();
    decorateLinks(root);
    syncClientAuthFromServer(this);
    lastAuthProbeAt = Date.now();
    refreshHaptics(this);
    refreshTheme(this);
    syncInstall(this);
    refreshLastGeneration(this);
    syncLastGeneration(this, { force: true });
    refreshDraft(this);
    window.FlorameAvatar?.paint?.(this.root);
  }

  AccountCenter.prototype.bind = function () {
    var self = this;
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-fac-close]'), function (button) {
      button.addEventListener('click', function () { self.close(); });
    });
    if (this.notifications) this.notifications.addEventListener('click', function () { togglePush(self); });
    if (this.location) this.location.addEventListener('click', function () { requestLocationAccess(self); });
    if (this.haptics) this.haptics.addEventListener('click', function () { toggleHaptics(self); });
    if (this.language) this.language.addEventListener('change', function () { changeLanguage(self); });
    if (this.install) this.install.addEventListener('click', function () { openInstall(self); });
    if (this.helpButton) this.helpButton.addEventListener('click', function () { self.openHelp(); });
    if (this.helpBack) this.helpBack.addEventListener('click', function () { self.closeHelp(); });
    this.root.querySelectorAll('[data-fac-avatar-open]').forEach(function (button) { button.addEventListener('click', function () { openAvatar(self,button); }); });
    this.root.addEventListener('florame:sheet-back', function (event) {
      if (self.root.classList.contains('is-avatar-view')) { event.preventDefault(); window.FlorameAvatarEditor?.close?.(); }
      else if (self.root.classList.contains('is-help-view')) { event.preventDefault(); self.closeHelp(); }
    });
    if (this.lastGenerationOpen) this.lastGenerationOpen.addEventListener('click', function () { openLastGeneration(self); });
    if (this.lastGenerationImage) this.lastGenerationImage.addEventListener('error', function () {
      self.lastGenerationImage.hidden = true;
      self.lastGenerationImage.removeAttribute('src');
      if (self.lastGenerationFallback) self.lastGenerationFallback.hidden = false;
    });
    Array.prototype.forEach.call(this.chatButtons, function (button) {
      button.addEventListener('click', function (event) { self.openChat(event); });
    });
    this.root.addEventListener('keydown', function (event) {
      if (self.panel.hasAttribute('data-florame-bottom-sheet-enhanced')) return;
      if (event.key === 'Escape') { event.preventDefault(); self.close(); return; }
      if (event.key !== 'Tab') return;
      var items = focusables(self.panel);
      if (!items.length) { event.preventDefault(); self.panel.focus(); return; }
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    this.bindSwipeToClose();
  };

  AccountCenter.prototype.bindSwipeToClose = function () {
    var self = this;
    var surfaces = this.root.querySelectorAll('[data-fac-drag-surface]');

    function resetDrag() {
      self.dragState = null;
      self.root.classList.remove('is-dragging');
      self.root.style.removeProperty('--fac-drag-y');
      self.root.style.removeProperty('--fac-drag-backdrop');
    }

    function onPointerDown(event) {
      if (self.panel?.hasAttribute('data-florame-bottom-sheet-enhanced')) return;
      if (self.root.hidden || !self.root.classList.contains('is-open')) return;
      if (event.button != null && event.button !== 0) return;
      if (event.target && event.target.closest && event.target.closest('button,a,input,select,textarea')) return;
      self.dragState = {
        pointerId: event.pointerId,
        startY: event.clientY,
        lastY: event.clientY,
        lastTime: event.timeStamp || Date.now(),
        velocity: 0
      };
      self.root.classList.remove('is-swipe-closing');
      self.root.classList.add('is-dragging');
      try { event.currentTarget.setPointerCapture(event.pointerId); } catch (_) {}
    }

    function onPointerMove(event) {
      var state = self.dragState;
      if (!state || state.pointerId !== event.pointerId) return;
      var y = Number(event.clientY || 0);
      var delta = Math.max(0, y - state.startY);
      var now = event.timeStamp || Date.now();
      var elapsed = Math.max(1, now - state.lastTime);
      state.velocity = Math.max(0, (y - state.lastY) / elapsed);
      state.lastY = y;
      state.lastTime = now;
      self.root.style.setProperty('--fac-drag-y', delta.toFixed(1) + 'px');
      self.root.style.setProperty('--fac-drag-backdrop', String(Math.max(.18, 1 - delta / Math.max(280, window.innerHeight * .72))));
      if (delta > 4) event.preventDefault();
    }

    function onPointerEnd(event) {
      var state = self.dragState;
      if (!state || state.pointerId !== event.pointerId) return;
      var delta = Math.max(0, Number(event.clientY || state.lastY) - state.startY);
      var threshold = Math.max(92, Math.min(140, window.innerHeight * .13));
      var shouldClose = event.type !== 'pointercancel' && (delta >= threshold || (delta >= 48 && state.velocity > .55));
      try { event.currentTarget.releasePointerCapture(event.pointerId); } catch (_) {}
      resetDrag();
      if (shouldClose) {
        self.root.classList.add('is-swipe-closing');
        self.close();
      }
    }

    Array.prototype.forEach.call(surfaces, function (surface) {
      surface.addEventListener('pointerdown', onPointerDown);
      surface.addEventListener('pointermove', onPointerMove);
      surface.addEventListener('pointerup', onPointerEnd);
      surface.addEventListener('pointercancel', onPointerEnd);
    });
  };

  AccountCenter.prototype.open = function (trigger, restoreTriggerFocusOnClose) {
    var self = this;
    if (!accountStylesReady()) {
      var styleEpoch = this.styleOpenEpoch = (this.styleOpenEpoch || 0) + 1;
      if (trigger) trigger.setAttribute('aria-busy', 'true');
      return ensureAccountStyles().then(function () {
        if (self.styleOpenEpoch === styleEpoch) self.open(trigger, restoreTriggerFocusOnClose);
      }).catch(function () {
        if (self.styleOpenEpoch !== styleEpoch) return;
        window.FlorameUI?.toast({ tone: 'error', title: languageOf(self.root) === 'en' ? 'Menu unavailable' : 'Menù non disponibile', detail: languageOf(self.root) === 'en' ? 'Check your connection and try again.' : 'Controlla la connessione e riprova.' });
      }).finally(function () { if (trigger) trigger.removeAttribute('aria-busy'); });
    }
    if (this.closeTimer) { window.clearTimeout(this.closeTimer); this.closeTimer = 0; }
    if (this.openFrame) window.cancelAnimationFrame(this.openFrame);
    this.lastTrigger = trigger || document.activeElement;
    this.restoreTriggerFocusOnClose = restoreTriggerFocusOnClose !== false;
    delete this.pendingCloseFocus;
    this.dragState = null;
    this.root.classList.remove('is-dragging', 'is-swipe-closing');
    this.root.style.removeProperty('--fac-drag-y');
    this.root.style.removeProperty('--fac-drag-backdrop');
    this.root.hidden = false;
    window.FlorameBottomSheets?.resumeOpen?.(this.root);
    refreshDraft(this);
    var settings = this.root.querySelector('.fac__settings');
    if (settings) settings.open = false;
    document.body.classList.add('fac-scroll-locked');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    // The shared sheet engine supplies the entering pose without a forced layout.
    if (!this.panel.hasAttribute('data-florame-bottom-sheet-enhanced')) this.panel.getBoundingClientRect();
    this.openFrame = window.requestAnimationFrame(function () {
      self.openFrame = 0;
      if (self.root.hidden) return;
      self.root.classList.add('is-open');
      self.panel.focus({ preventScroll: true });
      // Give the opening surface one frame before refreshing storage, auth and push.
      window.requestAnimationFrame(function () {
        if (self.root.hidden) return;
        refreshLastGeneration(self);
        syncLastGeneration(self);
        refreshCapabilities(self);
        ensureAuthFresh(self);
        window.FlorameAvatar?.load?.().catch(function () {});
      });
    });
    emit('florame:account-center-open', { authenticated: this.root.getAttribute('data-authenticated') === 'true' });
  };

  AccountCenter.prototype.clearHelpContext = function () {
    this.root.querySelector('[data-fac-help-context]')?.remove();
    var email = this.helpView?.querySelector('.fac__contacts a[href^="mailto:"]');
    if (email && this.helpEmailHref) email.setAttribute('href', this.helpEmailHref);
    this.helpContext = null;
  };

  AccountCenter.prototype.setHelpContext = function (context) {
    this.clearHelpContext();
    if (!context?.summary || !this.helpView) return;
    this.helpContext = context;
    var self = this, en = languageOf(this.root) === 'en';
    var group = document.createElement('div');
    group.className = 'fac__faq fac__message-context';
    group.setAttribute('data-fac-help-context', '');
    var details = document.createElement('details'), summary = document.createElement('summary');
    summary.textContent = en ? 'Details to share with support' : 'Dettagli utili da condividere';
    details.appendChild(summary);
    var copy = document.createElement('p');
    copy.textContent = [context.title, context.description].filter(Boolean).join(' · ');
    details.appendChild(copy);
    var input = document.createElement('textarea');
    input.readOnly = true; input.value = context.summary;
    input.setAttribute('aria-label', summary.textContent);
    details.appendChild(input);
    var note = document.createElement('p');
    note.textContent = en ? 'Copy these details into the support chat; the email already includes them.' : 'Copia questi dettagli nella chat di assistenza; nell’email sono già preparati.';
    details.appendChild(note);
    var button = document.createElement('button'); button.type = 'button';
    button.className = 'fac__help-back'; button.textContent = en ? 'Copy details' : 'Copia dettagli';
    button.addEventListener('click', async function () {
      try {
        await navigator.clipboard.writeText(context.summary);
        if (self.helpContext === context && self.chatStatus) self.chatStatus.textContent = en ? 'Details copied. Paste them into the support chat.' : 'Dettagli copiati. Incollali nella chat di assistenza.';
      } catch (_) { if (self.helpContext === context) { input.focus(); input.select(); } }
    });
    details.appendChild(button);
    if (typeof context.onReturn === 'function') {
      var back = document.createElement('button'); back.type = 'button'; back.className = 'fac__help-back';
      back.textContent = en ? (context.canViewOrder ? 'View order details' : 'Back to florist chat') : context.returnLabel;
      back.addEventListener('click', function () {
        var observer = new MutationObserver(function () {
          if (!self.root.hidden) return;
          observer.disconnect(); context.onReturn();
        });
        observer.observe(self.root, { attributes: true, attributeFilter: ['hidden'] });
        self.close(false);
        window.setTimeout(function () { observer.disconnect(); }, 1500);
      });
      details.appendChild(back);
    }
    group.appendChild(details);
    this.helpView.querySelector('.fac__help-intro').after(group);
    var email = this.helpView.querySelector('.fac__contacts a[href^="mailto:"]');
    if (email) {
      this.helpEmailHref = email.getAttribute('href');
      email.setAttribute('href', this.helpEmailHref + '&body=' + encodeURIComponent(context.summary));
    }
  };

  AccountCenter.prototype.openHelp = function (context) {
    if (!this.helpView) return;
    this.setHelpContext(context);
    var c = copyFor(this.root);
    this.helpView.hidden = false;
    this.root.classList.add('is-help-view');
    if (this.title) this.title.textContent = c.helpTitle;
    if (this.description) this.description.textContent = c.helpDescription;
    if (this.chatStatus) this.chatStatus.textContent = '';
    if (this.scroll) this.scroll.scrollTop = 0;
    var firstSummary = this.helpView.querySelector('summary');
    if (firstSummary && typeof firstSummary.focus === 'function') firstSummary.focus({ preventScroll: true });
    this.animateView(1);
    emit('florame:account-help-open');
  };

  AccountCenter.prototype.closeHelp = function (restoreFocus) {
    this.clearHelpContext();
    var wasOpen = this.root.classList.contains('is-help-view');
    this.root.classList.remove('is-help-view');
    if (this.helpView) this.helpView.hidden = true;
    if (this.title) this.title.textContent = this.accountTitle;
    if (this.description) this.description.textContent = this.accountDescription;
    if (this.chatStatus) this.chatStatus.textContent = '';
    if (this.scroll) this.scroll.scrollTop = 0;
    if (restoreFocus !== false && this.helpButton && typeof this.helpButton.focus === 'function') this.helpButton.focus({ preventScroll: true });
    if (wasOpen && restoreFocus !== false) this.animateView(-1);
  };

  AccountCenter.prototype.animateView = function (direction) {
    if (!this.scroll || this.root.hidden || !this.root.classList.contains('is-open')) return;
    if (this.viewAnimation) this.viewAnimation.cancel();
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.viewAnimation = this.scroll.animate([
      { opacity: .35, transform: reduced ? 'none' : 'translateX(' + (direction * 14) + 'px)' },
      { opacity: 1, transform: 'none' }
    ], { duration: reduced ? 100 : 180, easing: 'cubic-bezier(.2,.8,.2,1)' });
  };

  AccountCenter.prototype.openChat = function (event) {
    var self = this;
    var c = copyFor(this.root);
    var sourceButton = event && event.currentTarget ? event.currentTarget : null;
    try { event.preventDefault(); event.stopPropagation(); } catch (_) {}
    if (sourceButton) sourceButton.setAttribute('aria-busy', 'true');
    if (this.chatStatus) this.chatStatus.textContent = c.chatOpening;

    waitForShopifyInboxToggle().then(function (button) {
      if (!button) {
        if (sourceButton) sourceButton.removeAttribute('aria-busy');
        if (self.chatStatus) self.chatStatus.textContent = c.chatUnavailable;
        emit('florame:shopify-inbox-unavailable');
        return;
      }

      self.close(false);
      window.setTimeout(function () {
        var controller = window.FlorameShopifyInbox;
        var opener = controller && typeof controller.open === 'function'
          ? controller.open
          : window.openShopifyInboxFromMenu;
        var result = typeof opener === 'function'
          ? opener({ preventDefault: function () {}, stopPropagation: function () {} })
          : false;
        Promise.resolve(result).then(function (opened) {
          if (sourceButton) sourceButton.removeAttribute('aria-busy');
          if (opened === false) {
            emit('florame:shopify-inbox-unavailable');
            return;
          }
          emit('florame:shopify-inbox-opened');
        });
      }, 260);
    });
  };

  AccountCenter.prototype.close = function (restoreFocus) {
    var self = this;
    this.styleOpenEpoch = (this.styleOpenEpoch || 0) + 1;
    if (this.openFrame) { window.cancelAnimationFrame(this.openFrame); this.openFrame = 0; }
    if (this.root.hidden) return;
    if (this.panel.hasAttribute('data-florame-bottom-sheet-enhanced') && !this.root.__florameBottomSheetReplayClose && this.root.classList.contains('is-open')) {
      this.pendingCloseFocus = restoreFocus;
      if (this.root.__florameBottomSheetClosing || window.FlorameBottomSheets?.requestClose?.(this.root)) return;
    }
    if (Object.prototype.hasOwnProperty.call(this, 'pendingCloseFocus')) {
      restoreFocus = this.pendingCloseFocus;
      delete this.pendingCloseFocus;
    }
    if (this.viewAnimation) this.viewAnimation.cancel();
    this.root.classList.remove('is-open');
    document.body.classList.remove('fac-scroll-locked');
    if (this.lastTrigger) this.lastTrigger.setAttribute('aria-expanded', 'false');
    this.closeTimer = window.setTimeout(function () {
      self.root.hidden = true;
      self.root.classList.remove('is-dragging', 'is-swipe-closing');
      self.root.style.removeProperty('--fac-drag-y');
      self.root.style.removeProperty('--fac-drag-backdrop');
      self.dragState = null;
      self.closeHelp(false);
      self.closeTimer = 0;
    }, this.root.__florameBottomSheetReplayClose ? 0 : 240);
    var shouldRestoreFocus = restoreFocus === true || (restoreFocus !== false && this.restoreTriggerFocusOnClose);
    if (shouldRestoreFocus && this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
      try { this.lastTrigger.focus({ preventScroll: true }); } catch (_) { this.lastTrigger.focus(); }
    } else if (this.lastTrigger && typeof this.lastTrigger.blur === 'function') {
      // iOS Safari can keep a native ring around a tapped trigger after the sheet closes.
      // Pointer users do not need focus restoration; keyboard and assistive activation still do.
      this.lastTrigger.blur();
    }
    emit('florame:account-center-close');
  };

  function init(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var roots = [];
    if (scope && scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    Array.prototype.push.apply(roots, context.querySelectorAll(ROOT_SELECTOR));
    roots.forEach(function (root) {
      if (root.__florameAccountCenter) return;
      var instance = new AccountCenter(root);
      root.__florameAccountCenter = instance;
      instances.push(instance);
    });
    Array.prototype.forEach.call(document.querySelectorAll(TRIGGER_SELECTOR), function (trigger) {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-controls', 'FlorameAccountCenter');
      if (!trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'false');
      trigger.style.touchAction = 'manipulation';
    });
    return instances;
  }

  function firstInstance() {
    if (!instances.length) init(document);
    return instances[0] || null;
  }

  function openUtility(instance, kind, options) {
    if (!instance) return null;
    var utility = String(kind || '').toLowerCase();
    refreshCapabilities(instance);
    if (utility === 'install') {
      openInstall(instance);
      return instance;
    }
    if (utility === 'last_generation' || utility === 'latest_generation') {
      openLastGeneration(instance);
      return instance;
    }

    var opening = instance.open(options?.trigger || null, !!options?.trigger);
    Promise.resolve(opening).then(function () { window.requestAnimationFrame(function () {
      if (instance.root.hidden || instance.root.__florameBottomSheetClosing || !instance.root.classList.contains('is-open')) return;
      if (utility === 'help') {
        instance.openHelp(options?.helpContext);
        return;
      }
      if (utility === 'club' || utility === 'florame_club') {
        var clubEntry = instance.root.querySelector('[data-fac-loyalty-open], .fac__loyalty-entry--guest');
        if (!clubEntry || clubEntry.hidden || instance.root.getAttribute('data-loyalty-available') !== 'true') return;
        if (rootIsAuthenticated(instance.root) && window.FlorameCustomerLoyalty && typeof window.FlorameCustomerLoyalty.open === 'function') {
          window.setTimeout(function () { window.FlorameCustomerLoyalty.open(clubEntry); }, 120);
          return;
        }
        try { clubEntry.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) {}
        window.setTimeout(function () {
          try { clubEntry.focus({ preventScroll: true }); } catch (_) { clubEntry.focus(); }
        }, 180);
        return;
      }
      if (utility !== 'language' || !instance.language) return;
      try { instance.language.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) {}
      window.setTimeout(function () {
        try { instance.language.focus({ preventScroll: true }); } catch (_) { instance.language.focus(); }
      }, 180);
    }); });
    return instance;
  }

  function activateTrigger(trigger, restoreTriggerFocusOnClose) {
    var instance = firstInstance();
    if (instance) instance.open(trigger, restoreTriggerFocusOnClose);
    return instance;
  }

  function isCustomerLoginLink(anchor) {
    if (!anchor || !anchor.href || anchor.hasAttribute('data-florame-auth-native-bypass')) return false;
    try {
      var url = new URL(anchor.href, window.location.origin);
      var host = String(url.hostname || '').toLowerCase();
      var ownHost = String(window.location.hostname || '').toLowerCase();
      var normalizedPath = String(url.pathname || '').replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/)/i, '');
      return (host === ownHost || host === 'account.florame.ai') && (normalizedPath === CUSTOMER_LOGIN_PATH || normalizedPath === '/account/login');
    } catch (_) {
      return false;
    }
  }

  function loginLinkReturnTo(anchor) {
    if (!anchor || !anchor.href) return '';
    try {
      var url = new URL(anchor.href, window.location.origin);
      return url.searchParams.get('return_to') || url.searchParams.get('return_url') || '';
    } catch (_) {
      return '';
    }
  }

  function nativeAuthFallbackUrl(context) {
    var url = new URL(CUSTOMER_LOGIN_PATH, window.location.origin);
    url.searchParams.set('return_to', safeReturnTo(context && context.returnTo));
    return url.pathname + url.search;
  }

  function waitForNativeAccountApi(timeoutMs, closingRoot) {
    return new Promise(function (resolve) {
      var deadline = Date.now() + Math.max(0, Number(timeoutMs || 0));
      function check() {
        var api = window.FlorameShopifyAccountUi;
        var sheets = window.FlorameBottomSheets;
        var menuClosed = !closingRoot || closingRoot.hidden;
        var historySettled = !sheets || typeof sheets.isHistorySettled !== 'function' || sheets.isHistorySettled();
        // Opening during the previous sheet's exit lets its queued history.back
        // cancel a quick/autofilled email submission in WebKit.
        if (menuClosed && historySettled && api && typeof api.open === 'function') { resolve(api); return; }
        if (Date.now() >= deadline) { resolve(null); return; }
        window.setTimeout(check, 32);
      }
      check();
    });
  }

  function openNativeAuth(instance, context, trigger) {
    var delay = instance && !instance.root.hidden ? 260 : 0;
    if (delay) instance.close(false);

    window.setTimeout(function () {
      waitForNativeAccountApi(1200, instance && instance.root).then(function (nativeAccount) {
        return nativeAccount
          ? nativeAccount.open({ trigger: trigger || null, intent: context.intent, returnTo: context.returnTo })
          : false;
      }).then(function (opened) {
        if (opened) return;
        emit('florame:shopify-account-unavailable', {
          intent: context.intent,
          returnTo: context.returnTo
        });
        window.location.assign(nativeAuthFallbackUrl(context));
      }).catch(function () {
        window.location.assign(nativeAuthFallbackUrl(context));
      });
    }, delay);
  }

  function requestAuth(options) {
    var opts = options || {};
    var instance = firstInstance();
    var context = {
      returnTo: safeReturnTo(opts.returnTo),
      intent: String(opts.intent || 'account').trim().slice(0, 80) || 'account'
    };
    if (!opts.force && ((instance && rootIsAuthenticated(instance.root)) || isNativeAccountActive())) {
      emit('florame:auth-ready', {
        authenticated: true,
        intent: context.intent,
        returnTo: context.returnTo
      });
      return instance;
    }
    context = rememberAuthContext(instance && instance.root, context);
    if (instance) decorateLinks(instance.root);
    emit('florame:auth-requested', {
      intent: context.intent,
      returnTo: context.returnTo,
      trigger: opts.trigger || null
    });
    openNativeAuth(instance, context, opts.trigger || null);
    return instance;
  }

  function authOptionsFromTrigger(trigger) {
    var requestedReturnTo = trigger.getAttribute('data-florame-auth-return-to') || loginLinkReturnTo(trigger);
    return {
      intent: trigger.getAttribute('data-florame-auth-intent') || 'account',
      returnTo: requestedReturnTo || (window.location.pathname + window.location.search + window.location.hash),
      trigger: trigger
    };
  }

  function cancelPointerActivation(trigger) {
    if (trigger && pendingPointerTrigger && trigger !== pendingPointerTrigger) return;
    if (pointerActivationTimer) window.clearTimeout(pointerActivationTimer);
    pointerActivationTimer = 0;
    pendingPointerTrigger = null;
  }

  function schedulePointerActivation(trigger) {
    cancelPointerActivation();
    pendingPointerTrigger = trigger;
    pointerActivationTimer = window.setTimeout(function () {
      var queuedTrigger = pendingPointerTrigger;
      pointerActivationTimer = 0;
      pendingPointerTrigger = null;
      if (queuedTrigger && queuedTrigger.isConnected !== false) activateTrigger(queuedTrigger, false);
    }, 380);
  }

  document.addEventListener('pointerup', function (event) {
    if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
    var trigger = event.target && event.target.closest ? event.target.closest(TRIGGER_SELECTOR) : null;
    if (!trigger) return;
    schedulePointerActivation(trigger);
  }, true);

  document.addEventListener('click', function (event) {
    var logoutLink = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (logoutLink && (logoutLink.hasAttribute('data-native-account-logout') || logoutLink.getAttribute('data-fac-auth-route') === 'logout' || logoutLink.classList.contains('profile-menu-item--logout') || /\/account\/logout(?:[?#]|$)/.test(logoutLink.href))) {
      emit('florame:auth-logout');
      applyWebCustomerIdentity('', '');
      try { window.sessionStorage.removeItem(AUTH_CONTEXT_KEY); } catch (_) {}
      if (window.FlorameNativeAuth && window.FlorameNativeAuth.isAvailable && typeof window.FlorameNativeAuth.signOut === 'function') {
        event.preventDefault();
        window.FlorameNativeAuth.signOut().then(function () { window.location.assign(logoutLink.href); });
      }
      return;
    }
    var accountLink = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    var accountInstance = firstInstance();
    if (accountLink && !rootIsAuthenticated(accountInstance && accountInstance.root) && !isNativeAccountActive()) {
      var accountTarget;
      try { accountTarget = new URL(accountLink.href, window.location.origin); } catch (_) {}
      if (accountTarget && accountTarget.origin === window.location.origin && /^\/(?:[a-z]{2}\/)?pages\/florame-account\/?$/.test(accountTarget.pathname)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        requestAuth({ intent: 'account', returnTo: accountTarget.pathname + accountTarget.search, trigger: accountLink });
        return;
      }
    }
    var authTrigger = event.target && event.target.closest ? event.target.closest(AUTH_TRIGGER_SELECTOR) : null;
    if (!authTrigger && event.target && event.target.closest) {
      var loginLink = event.target.closest('a[href]');
      if (isCustomerLoginLink(loginLink)) authTrigger = loginLink;
    }
    if (authTrigger) {
      event.preventDefault();
      event.stopPropagation();
      requestAuth(authOptionsFromTrigger(authTrigger));
      return;
    }
    var trigger = event.target && event.target.closest ? event.target.closest(TRIGGER_SELECTOR) : null;
    if (!trigger) return;
    event.preventDefault();
    cancelPointerActivation(trigger);
    activateTrigger(trigger, event.detail === 0);
  }, true);

  window.addEventListener('florame:account-utility', function (event) {
    var utility = event && event.detail ? event.detail.utility : '';
    openUtility(firstInstance(), utility);
  });
  window.addEventListener('florame:request-auth', function (event) {
    requestAuth(event && event.detail ? event.detail : {});
  });

  window.addEventListener('pagehide', function () {
    instances.forEach(function (instance) { instance.styleOpenEpoch = (instance.styleOpenEpoch || 0) + 1; });
  });
  window.addEventListener('pageshow', function (event) {
    instances.forEach(function (instance) {
      refreshCapabilities(instance);
      refreshLastGeneration(instance);
      if (event && event.persisted) ensureAuthFresh(instance, { force: true });
      else syncClientAuthFromServer(instance);
    });
  });
  window.addEventListener('focus', function () {
    var instance = firstInstance();
    if (instance) {
      ensureAuthFresh(instance);
      refreshLastGeneration(instance);
    }
  });
  window.addEventListener('storage', function (event) {
    if (!event || event.key === LAST_GENERATION_KEY || event.key === LAST_GENERATION_ACTIVE_KIND_KEY || (event.key && event.key.indexOf(LAST_GENERATION_ARCHIVE_KEY_PREFIX) === 0)) instances.forEach(refreshLastGeneration);
  });
  window.addEventListener('florame:create-draft-change', function () { instances.forEach(refreshDraft); });
  window.addEventListener('pageshow', function () { instances.forEach(refreshDraft); });
  window.addEventListener('florame:background-loading-change', function () { instances.forEach(refreshLastGeneration); });
  window.addEventListener('florame:background-loading-clear', function () { instances.forEach(refreshLastGeneration); });
  window.addEventListener('florame:last-generation-persisted', function () { instances.forEach(function (instance) { refreshLastGeneration(instance); syncLastGeneration(instance, { force: true }); }); });
  document.addEventListener('visibilitychange', function () {
    document.documentElement.toggleAttribute('data-florame-page-hidden', document.visibilityState !== 'visible');
    if (document.visibilityState !== 'visible') return;
    instances.forEach(function (item) { refreshPush(item); refreshLocation(item); });
    var instance = firstInstance();
    if (instance) {
      ensureAuthFresh(instance);
      refreshLastGeneration(instance);
    }
  });
  window.addEventListener('florame:web-push-state', function (event) {
    if (nativePushAvailable()) return;
    instances.forEach(function (instance) {
      instance.pushRefreshId = (instance.pushRefreshId || 0) + 1;
      applyPushState(instance, event && event.detail ? event.detail : {});
    });
  });
  window.addEventListener('florame:web-push-ready', function () { instances.forEach(refreshPush); });
  window.addEventListener('florame:native-permissions', function (event) {
    if (event.detail && event.detail.location) instances.forEach(function (instance) { renderLocation(instance, event.detail.location); });
    var state = event.detail && event.detail.notifications;
    if (!state || !nativePushAvailable()) return;
    instances.forEach(function (instance) {
      instance.pushRefreshId = (instance.pushRefreshId || 0) + 1;
      if (state.permission !== 'granted') {
        instance.nativePushActive = false;
        applyPushState(instance, Object.assign({ supported: true, subscribed: false, platform: 'native' }, state), { action: true });
      }
      refreshPush(instance);
    });
  });
  window.addEventListener('florame:native-push-token', function () { instances.forEach(refreshPush); });
  window.addEventListener('florame:hapticschange', function () { instances.forEach(refreshHaptics); });
  window.addEventListener('florame:pwa-install-state', function (event) {
    instances.forEach(function (instance) { syncInstall(instance, event && event.detail ? event.detail : {}); });
  });
  window.addEventListener('florame:native-ready', function () { instances.forEach(refreshCapabilities); });
  window.addEventListener('florame:partner-ready', function () { instances.forEach(function (instance) { decorateLinks(instance.root); }); });
  window.addEventListener('florame:native-language-change', function (event) {
    var next = event && event.detail && event.detail.language === 'en' ? 'en' : 'it';
    instances.forEach(function (instance) {
      instance.root.setAttribute('data-language', next);
      instance.accountTitle = next === 'en' ? 'Menu' : 'Menù';
      if (!instance.root.classList.contains('is-help-view') && !instance.root.classList.contains('is-avatar-view') && instance.title) instance.title.textContent = instance.accountTitle;
      if (instance.language) instance.language.value = next;
      refreshCapabilities(instance);
      refreshLastGeneration(instance);
    });
  });
  window.FlorameAccountCenter = Object.assign({}, window.FlorameAccountCenter || {}, {
    version: '1.13.0',
    init: init,
    open: function (trigger) { var instance = firstInstance(); if (instance) instance.open(trigger || null, true); return instance; },
    openAvatar: function (trigger) { var instance = firstInstance(); if (instance) { Promise.resolve(instance.open(trigger || null,true)).then(function () { requestAnimationFrame(function () { openAvatar(instance,trigger); }); }); } return instance; },
    requestAuth: requestAuth,
    openUtility: function (kind, options) { return openUtility(firstInstance(), kind, options); },
    install: function () { return openUtility(firstInstance(), 'install'); },
    hasLastGeneration: function () {
      var instance = firstInstance();
      return !!(instance && refreshLastGeneration(instance));
    },
    openLastGeneration: function () {
      var instance = firstInstance();
      if (instance) openLastGeneration(instance);
      return instance;
    },
    canInstall: function () {
      var instance = firstInstance();
      if (!instance) return false;
      syncInstall(instance);
      return !!(instance.install && !instance.install.hidden);
    },
    close: function () { instances.forEach(function (instance) { instance.close(); }); },
    refreshCapabilities: function () { return Promise.all(instances.map(refreshCapabilities)); },
    refreshAuth: function () { var instance = firstInstance(); return instance ? ensureAuthFresh(instance, { force: true }) : Promise.resolve(true); },
    decorateLinks: function () { instances.forEach(function (instance) { decorateLinks(instance.root); }); },
    refreshLastGeneration: function () { return instances.map(refreshLastGeneration); }
  });

  if (window.__floramePendingAuthRequest) {
    var pendingAuthRequest = window.__floramePendingAuthRequest;
    window.__floramePendingAuthRequest = null;
    window.requestAnimationFrame(function () { requestAuth(pendingAuthRequest); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () {
    init(document);
    restoreAuthContext(firstInstance());
  }, { once: true });
  else {
    init(document);
    restoreAuthContext(firstInstance());
  }
})();
